/**
 * 本棚・閲覧履歴の埋め込みウィジェット（EP-1）。
 *
 * 本棚と履歴は「書庫横断」の概念なので、1 冊を開いているビューワの中ではなく、
 * 一覧ページへ置く。書庫横断検索（`librarySearch`）と同じ形の公開ファクトリで、
 * ビューワ本体とは独立に使える。認可はサーバ側（`/me/*`）が担う。
 */
import { cls } from '../brand';
import type { I18nOptions } from '../core/options';
import { createTranslator } from '../i18n/i18n';
import type { ListType, UserItem } from './store';

export interface UserListOptions {
  /** API の基準 URL（既定 '/api/v1'）。 */
  base?: string;
  /** 項目を開くリンク先。既定は `?slug=<slug>#page=<page>`。 */
  linkTo?: (item: UserItem) => string;
  /** 文言辞書の部分上書き（FR-I18N-01）。 */
  i18n?: Partial<I18nOptions>;
}

export interface UserListHandle {
  /** 一覧を取り直す。 */
  refresh(): Promise<void>;
  dispose(): void;
}

/** 本棚（お気に入り）の一覧。 */
export function userShelf(el: HTMLElement, options: UserListOptions = {}): UserListHandle {
  return userList(el, 'shelf', 'shelf.empty', options);
}

/** 閲覧履歴（最終閲覧の新しい順）。 */
export function readingHistory(el: HTMLElement, options: UserListOptions = {}): UserListHandle {
  return userList(el, 'history', 'history.empty', options);
}

function userList(
  el: HTMLElement,
  type: ListType,
  emptyKey: string,
  options: UserListOptions,
): UserListHandle {
  const doc = el.ownerDocument;
  const base = options.base ?? '/api/v1';
  const linkTo =
    options.linkTo ??
    ((item: UserItem): string =>
      `?slug=${encodeURIComponent(item.book)}${item.page ? `#page=${item.page}` : ''}`);
  const translate = createTranslator(
    { locale: options.i18n?.locale ?? 'auto', strings: options.i18n?.strings ?? {} },
    doc,
  );

  el.classList.add(cls('userlist'));
  const status = doc.createElement('p');
  status.className = cls('userlist-status');
  status.setAttribute('aria-live', 'polite');
  const list = doc.createElement('ul');
  list.className = cls('userlist-items');
  el.append(status, list);

  let controller: AbortController | null = null;

  const render = (items: UserItem[]): void => {
    status.textContent = items.length === 0 ? translate(emptyKey) : '';
    const fragment = doc.createDocumentFragment();
    for (const item of items) {
      const li = doc.createElement('li');
      const link = doc.createElement('a');
      link.className = cls('userlist-item');
      link.href = linkTo(item);

      const title = doc.createElement('span');
      title.className = cls('userlist-item-title');
      title.textContent = item.title ?? item.book;
      link.appendChild(title);

      if (item.page) {
        const meta = doc.createElement('span');
        meta.className = cls('userlist-item-meta');
        meta.textContent = translate('history.page', { page: item.page });
        link.appendChild(meta);
      }

      li.appendChild(link);
      fragment.appendChild(li);
    }
    list.replaceChildren(fragment);
  };

  const refresh = async (): Promise<void> => {
    controller?.abort();
    const ctrl = new AbortController();
    controller = ctrl;
    try {
      const response = await fetch(`${base}/me/data/${type}`, {
        credentials: 'same-origin',
        signal: ctrl.signal,
      });
      if (ctrl.signal.aborted) return;
      // 未ログイン（401）は「空」として扱う — 導入者が案内を出せるよう例外にはしない
      const data = response.ok ? ((await response.json()) as { items?: UserItem[] }) : { items: [] };
      render(data.items ?? []);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      render([]);
    }
  };

  void refresh();

  return {
    refresh,
    dispose(): void {
      controller?.abort();
      el.classList.remove(cls('userlist'));
      status.remove();
      list.remove();
    },
  };
}
